// Dependencies
import React, { Component } from 'react';

import classnames from 'classnames';
// eslint-disable-next-line lodash/import-scope
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import { Tracking } from '@nerdwallet/nw-client-lib';
import GoogleMapsAutocomplete from '@nerdwallet/react-google-maps-autocomplete';

// CSS
require('./location-autocomplete.less');

const { formInputChanged } = Tracking;

const ZIP_REG_EX = {
  COMPLETE: /^\d{5}$/, // 5 digits
  INCOMPLETE: /^\d{1,4}$/, // 1-4 digits, too short to be a zip code yet
  RESOLVED: /.+\(\d{5}\)/, // city, ST (12345)
  INVALID: /^\d{6,}$/, // > 6 digits, too long to be a valid zip code
  ANY: /^\d{1,}$|.+\(\d{5}\)/, // either resolved or at least one digit
};

const ERROR = {
  CITY_NOT_FOUND: 'City Not Found',
  INVALID_ZIP: 'Invalid ZIP Code',
  INVALID_CITY_OR_ZIP: 'Invalid City or ZIP Code',
};

class LocationAutoComplete extends Component {
  static getFormattedCityFromObj({
    city = '',
    state = '',
    zip = '',
    purchaseAddress = null,
    refinanceAddress = null,
  }) {
    let formattedCity = city;

    // Flatten this address somewhere maybe fromProfile
    if (purchaseAddress) {
      formattedCity = `${purchaseAddress.city}, ${purchaseAddress.state}`;
    }

    if (refinanceAddress) {
      formattedCity = `${refinanceAddress.city}, ${refinanceAddress.state}`;
    }

    if (state) {
      formattedCity = `${city}, ${state}`;
    }

    if (zip) {
      return `${formattedCity}${zip !== undefined ? ` ${zip}` : ''}`;
    }

    return formattedCity;
  }

  static getCityObjFromString(address = '') {
    const locationInfo = address.split(',');
    const city = locationInfo[0].trim();
    const state = locationInfo[1]?.match(/\w+/)
      ? locationInfo[1]?.match(/\w+/)[0]
      : '';
    const zip = locationInfo[1]?.match(/\d+/)
      ? locationInfo[1]?.match(/\d+/)[0]
      : '';

    return { city, state, zip };
  }

  constructor(props) {
    super(props);
    const { data } = props;

    this.state = {
      error: null,
      value: LocationAutoComplete.getFormattedCityFromObj(data),
    };
  }

  componentDidUpdate(prevProps) {
    const { data: prevData } = prevProps;
    const { data } = this.props;
    const { value } = this.state;
    const formattedCurrentValue =
      LocationAutoComplete.getCityObjFromString(value);

    if (!isEqual(data, prevData) && !isEqual(formattedCurrentValue, data)) {
      // It can exists multiple instances of `<LocationAutoComplete>` at the same time (e.g: OnboardingModal & CityHeader)
      // when user selects any results from AutoComplete, we dispatch `onChange` from props, this will trigger `cityChange` action
      // that will update props (data). 'Cause we dispatch a different value from GoogleAPI (GoogleAPI returns country and we don't consume the value)
      // We need to keep all instances synced
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        value: LocationAutoComplete.getFormattedCityFromObj(data),
      });
    }
  }

  onInputChange = (newValue) => {
    this.setState({ value: newValue });
  };

  onInputBlur = () => {
    const { value } = this.state;

    if (!ZIP_REG_EX.INCOMPLETE.test(value) && !ZIP_REG_EX.INVALID.test(value))
      return;

    // all numbers, too small for a zipcode, flag as error
    this.setState({ error: ERROR.INVALID_ZIP });
  };

  // eslint-disable-next-line consistent-return
  onInputSelect = (value) => {
    const { error } = this.state;
    const { onChange, enableTracking, trackingFormName } = this.props;

    const { address_components: addressComponents } = value;

    // zipCode is stored with 'postal_code' type in Google Places API.
    const postalCodeAddress = addressComponents.filter((address) =>
      address.types.includes('postal_code')
    );

    // City is stored with 'locality' type in Google Places API.
    const locality = addressComponents.filter((address) =>
      address.types.includes('locality')
    );

    // Some neighborhoods (such as: Brooklyn, Queens, etc.) are stored with 'sublocality' type in Google Places API.
    const subLocality = addressComponents.filter((address) =>
      address.types.includes('sublocality')
    );

    // State is stored as 'administrative_area_level_1' type in Google API.
    const stateAddress = addressComponents.filter((address) =>
      address.types.includes('administrative_area_level_1')
    );

    const zip = postalCodeAddress[0]?.long_name;
    const state = stateAddress[0]?.short_name;
    const cityAddr = locality[0]?.long_name || subLocality[0]?.long_name;

    const city = state ? `${cityAddr}, ${state}` : cityAddr;

    // if we don't have city and zipCode information, we should throw an error.
    if (!cityAddr && !zip) {
      return this.setState({ error: ERROR.CITY_NOT_FOUND });
    }

    if (enableTracking) {
      const trackingValue = `${city}${zip ? ` (${zip})` : ''}`;
      formInputChanged({
        formName: trackingFormName,
        fieldName: 'LocationAutoComplete',
        fieldValue: trackingValue,
      });
    }

    // user may have typed a special character, but GoogleAPI may still have shown suggestions, when user selects any suggestion
    //  we need to clear the previous error
    if (error) {
      this.setState({ error: null }, () => onChange({ city, zip }));
    } else {
      onChange({ city, zip });
    }
  };

  validateSuggestions = (suggestionList) => {
    const { value } = this.state;
    // Showing errors if there aren't any suggestions for the entered input
    const errorMessage =
      value && suggestionList.length === 0 ? ERROR.INVALID_CITY_OR_ZIP : '';

    this.setState({ error: errorMessage });
  };

  render() {
    const { error, value } = this.state;
    const { placeholder, label, smallOnMobile } = this.props;

    /* The (regions) type collection instructs the Places service to return any result matching the following types:
        - locality
        - sublocality
        - postal_code
        - country
        - administrative_area_level_1
        - administrative_area_level_2
        Supported types: https://developers.google.com/maps/documentation/places/web-service/supported_types#table3
      */
    const autocompleteOptions = {
      types: ['(regions)'],
    };

    const classes = classnames('nw-location-autocomplete', {
      'small-on-mobile': smallOnMobile,
    });

    return (
      <div className={classes}>
        <GoogleMapsAutocomplete
          enableSelectOnEnter
          placeholder={placeholder}
          value={value}
          onChange={this.onInputChange}
          onBlur={this.onInputBlur}
          onSelect={this.onInputSelect}
          onSuggestionsChanged={this.validateSuggestions}
          autocompleteOptions={autocompleteOptions}
          label={label}
          errorMessage={error}
          error={Boolean(error)}
        />
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
LocationAutoComplete.displayName = 'LocationAutoComplete';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
LocationAutoComplete.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  trackingFormName: PropTypes.string.isRequired,
  enableTracking: PropTypes.bool,
  smallOnMobile: PropTypes.bool,
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
LocationAutoComplete.defaultProps = {
  enableTracking: true,
  onChange: () => {},
  placeholder: 'San Francisco, CA',
  smallOnMobile: true,
  label: undefined,
};

export default LocationAutoComplete;
